import { AnalyticsEvent, CHATBOT_SUBSCRIBE, CHATBOT_UNSUBSCRIBE } from '../../common/Analytics';
import type { ErrorReporter } from '../../common/ErrorReporter';
import DB, { Entry } from '../../db/DB';
import { ReplicantErrorCode } from '../../Errors';
import type { ChatbotMessageInstance, TelegramMessage, TemplateRendererAPI } from '../../ReplicantChatbot';
import { BackgroundJobs } from '../../server/BackgroundJobs';
import type { APIAccessInterface, ValidateChatbotSubFn } from '../../server/ServerReplicant';
import type { SendLineChatbotMessageOptions } from '../../server/ServerReplicantChatbot';
import { isAndroidDeviceTokenExpired, isAppleDeviceTokenExpired } from '../../utils/DeviceTokenUtils';
import { duration } from '../../utils/Utils';
import type { WebPushSubscription } from '../../utils/WebPush';
import type {
    ChatbotAPI,
    ChatbotDiscordMessageSend,
    ChatbotFacebookA2UMessageSend,
    ChatbotInstagramMessageSend,
    ChatbotLineMessageSend,
    ChatbotMessageSend,
    ChatbotMessengerMessageSend,
    ChatbotTelegramMessageSend,
} from '../ChatbotAPI';
import { DelayedAction } from '../ReplicantAPI';
import { MessageReservation as LineMessageReservation } from './LineMessageClient';
import type { RichMenuAPIClient } from './LineRichMenuAPIClient';
import { Fetcher } from './utils/Fetcher';

export type PostMessageToChatbotResult = {
    body: string;
    requestMs?: number;
    requestTries?: number;
    statusCode: number;
};

export type PostMessageToChatbotFn = (
    userId: string,
    payload: unknown,
    scheduledActionId?: string,
) => Promise<PostMessageToChatbotResult>;

export const trackSubscriptionUpdate = (
    userId: string,
    isSubscribed: boolean,
    opts?: { timestamp?: number },
): AnalyticsEvent => ({
    user_id: userId,
    ...(isSubscribed
        ? {
              event_type: CHATBOT_SUBSCRIBE,
          }
        : {
              event_type: CHATBOT_UNSUBSCRIBE,
          }),
    event_properties: {},
    user_properties: {
        isSubscribed,
    },
    time: opts?.timestamp,
});

export const defaultValidateChatbotSubscription: ValidateChatbotSubFn = async (opts) => {
    const { entry, userId, invokeTime, messageToSend, db, sendChatbotMessage, isDev, sendAnalyticsEvents } = opts;

    if (!entry) {
        throw new Error('Cannot validate subscription as user does not exist! ' + userId);
    }

    if (!entry.chatbotMetainfo?.timeFirstSubscribed) {
        // mark initial chatbotMetainfo and subscription.
        entry.chatbotMetainfo = await db.chatbot.markSubscribed(userId, true, invokeTime);
        await sendAnalyticsEvents([trackSubscriptionUpdate(userId, true)]);
    } else {
        const oneDay = 24 * 60 * 60 * 1000;

        // When running on a local server, we don't get postbacks from FB, so we can't validate.
        if (isDev) {
            return false;
        }

        if (!entry.chatbotMetainfo.messengerId) {
            if (invokeTime - entry.chatbotMetainfo.timeFirstSubscribed >= oneDay) {
                // A whole day has passed and we still don't have a messengerId?
                // Fishy, must have disabled notifications and then lied to us about it.
                // Mark as unsubscribed and throw an error for reporting.
                entry.chatbotMetainfo = await db.chatbot.markSubscribed(userId, false, invokeTime);
                await sendAnalyticsEvents([trackSubscriptionUpdate(userId, false)]);
                throw new Error('Have not received messengerId in 24hr.');
            }
        } else {
            // If we have a messenger id, check if we've seen a message soonish
            if (invokeTime - (entry.chatbotMetainfo.timeLastMessageSent || 0) >= oneDay) {
                if (messageToSend) {
                    // Retry sending a chatbot message and check if it's successful.
                    try {
                        const success = await sendChatbotMessage({
                            receiverId: userId,
                            senderId: userId,
                            message: messageToSend,
                        });

                        if (success) {
                            entry.chatbotMetainfo = await db.chatbot.markMessageSent(userId, invokeTime);
                        } else {
                            // Analyze what the error status is and if it's because they've unsubbed, then
                            // mark as unsubscribed and throw an error.
                            entry.chatbotMetainfo = await db.chatbot.markSubscribed(userId, false, invokeTime);
                            await sendAnalyticsEvents([trackSubscriptionUpdate(userId, false)]);
                            throw new Error('Could not send a message when validating subscription.');
                        }
                    } catch (e: any) {
                        // If it's a network error, let this pass, as it's not our fault.
                        if (e.code !== ReplicantErrorCode.network_error) {
                            throw e;
                        }
                    }
                }
            }
        }
    }

    return entry.chatbotMetainfo?.subscribed || false;
};

export function createPlatformChatbotAPI<TUserState>(opts: {
    apiAccess: APIAccessInterface<any>;
    richMenuAPIClient?: RichMenuAPIClient;
    userId: string;
    db: DB<TUserState>;
    entry: Entry<TUserState> | null;
    errorReporter?: ErrorReporter;
    makeLineMessageReservation: () => LineMessageReservation;
    validateChatbotSubscription: ValidateChatbotSubFn;
    invokeTime: number;
    delayedActions: DelayedAction[];
    fetcher: Fetcher;
    scheduledActionId?: string;
}): ChatbotAPI {
    const { db, entry, invokeTime, delayedActions, richMenuAPIClient, validateChatbotSubscription, scheduledActionId } =
        opts;
    const { chatbot, createChatbotRendererApi, dateNow, platform, sendAnalyticsEvents } = opts.apiAccess;

    const sendFacebookA2UMessage: ChatbotFacebookA2UMessageSend = (receiverId, message, messageOpts) => {
        delayedActions.push({
            type: 'action',
            action: () =>
                chatbot.sendFacebookA2UChatbotMessage({
                    receiverId,
                    senderId: opts.userId,
                    message,
                    delayMs: messageOpts?.delayMs,
                }),
            apiMethodName: 'api.chatbot.sendFacebookA2UMessage',
        });
    };

    const sendMessage: ChatbotMessageSend = (receiverId, message, options) => {
        delayedActions.push({
            type: 'action',
            action: () =>
                chatbot.sendChatbotMessage({
                    receiverId,
                    senderId: opts.userId,
                    message,
                    forceChannel: options?.forceChannel,
                }),
            apiMethodName: 'api.chatbot.sendMessage',
        });
    };

    const sendInstagramMessage: ChatbotInstagramMessageSend = (receiverId, message, messageOpts) => {
        delayedActions.push({
            type: 'action',
            action: () =>
                chatbot.sendInstagramChatbotMessage({
                    commentId: messageOpts?.commentId,
                    receiverId,
                    senderId: opts.userId,
                    message,
                }),
            apiMethodName: 'api.chatbot.sendInstagramMessage',
        });
    };

    const sendLineMessage: ChatbotLineMessageSend = (userId, message, { replyToken } = {}) => {
        const reservation = opts.makeLineMessageReservation();
        const options: SendLineChatbotMessageOptions =
            replyToken === undefined
                ? {
                      type: 'push',
                      receiverId: userId,
                      senderId: opts.userId,
                      message,
                      sendMessageToLine: reservation.fulfill,
                      scheduledActionId,
                  }
                : {
                      type: 'reply',
                      receiverId: userId,
                      senderId: opts.userId,
                      replyToken,
                      message,
                      sendMessageToLine: reservation.fulfill,
                      scheduledActionId,
                  };
        delayedActions.push({
            type: 'action',
            action: () => chatbot.sendLineChatbotMessage(options).finally(reservation.cancel),
            apiMethodName: 'api.chatbot.sendLineMessage',
        });
    };

    const sendMessengerMessage: ChatbotMessengerMessageSend = (receiverId, message) => {
        delayedActions.push({
            type: 'action',
            action: () => chatbot.sendMessengerChatbotMessage({ receiverId, senderId: opts.userId, message }),
            apiMethodName: 'api.chatbot.sendMessengerMessage',
        });
    };

    const sendDiscordMessage: ChatbotDiscordMessageSend = (receiverId, message) => {
        delayedActions.push({
            type: 'action',
            action: () => chatbot.sendDiscordChatbotMessage({ receiverId, senderId: opts.userId, message }),
            apiMethodName: 'api.chatbot.sendDiscordMessage',
        });
    };

    const sendTelegramMessage: ChatbotTelegramMessageSend = ({
        chatId,
        maxDelayMs,
        message,
        priority,
        receiverId,
        replyToMessageId,
        pinned,
    }) => {
        const rendererApi = createChatbotRendererApi({ receiverId, senderId: opts.userId });

        // If `maxDelayMs` is defined, send message in a background job:
        if (maxDelayMs) {
            if (maxDelayMs < 0 || maxDelayMs > duration({ days: 1 })) {
                throw Error(`maxDelayMs must be between 0 and ${duration({ days: 1 })}`);
            }

            if (BackgroundJobs.isEnabled()) {
                BackgroundJobs.sendTelegramChatbotMessage({
                    analyticsUserProperties: message.analyticsUserProperties,
                    chatId,
                    maxDelayMs,
                    message: renderTelegramMessage(message, rendererApi),
                    payload: message.payload,
                    pinned,
                    priority,
                    receiverId,
                    replyToMessageId,
                    templateName: message.name,
                });

                return;
            }
        }

        // ...otherwise send message immediately:
        delayedActions.push({
            type: 'action',
            action: () =>
                chatbot.sendTelegramChatbotMessage({
                    analyticsUserProperties: message.analyticsUserProperties,
                    chatId,
                    message: renderTelegramMessage(message, rendererApi),
                    payload: message.payload,
                    pinned,
                    receiverId,
                    replyToMessageId,
                    templateName: message.name,
                }),
            apiMethodName: 'api.chatbot.sendTelegramMessage',
        });
    };

    function validateSubscription(messageToSend?: ChatbotMessageInstance) {
        const userId = opts.userId;

        const isDev = process.env.NODE_ENV === 'development';

        const action = async () => {
            await validateChatbotSubscription({
                entry: opts.entry,
                sendChatbotMessage: (...args) => chatbot.sendChatbotMessage(...args).then((result) => result.ok),
                userId,
                invokeTime,
                db,
                isDev,
                fetcher: opts.fetcher,
                messageToSend,
                sendAnalyticsEvents,
            }).catch(async (error) => {
                if (typeof error === 'object') {
                    error.name = 'ChatbotSubscriptionValidationError';
                }

                await opts.errorReporter?.captureException(error, undefined, { userId, messageToSend });
            });
        };
        delayedActions.push({ type: 'action', action, apiMethodName: 'api.chatbot.validateSubscription' });
    }

    function unsubscribe() {
        const userId = opts.userId;
        delayedActions.push({
            type: 'action',
            apiMethodName: 'api.chatbot.unsubscribe',
            action: async () => {
                if (!entry) {
                    throw new Error('Cannot mark user as unsubscribed as the user does not exist: ' + userId);
                }

                entry.chatbotMetainfo = await db.chatbot.markSubscribed(userId, false, invokeTime);
            },
        });
        delayedActions.push({
            type: 'sendAnalyticsEvents',
            events: [trackSubscriptionUpdate(userId, false)],
        });
    }

    function getChatbotMetainfo() {
        return entry?.chatbotMetainfo ?? null;
    }

    function getAppleDeviceTokenUpdatedAt() {
        return getChatbotMetainfo()?.appleDeviceTokenUpdatedAt || 0;
    }

    function getAndroidDeviceTokenUpdatedAt() {
        return getChatbotMetainfo()?.androidDeviceTokenUpdatedAt || 0;
    }

    function setAppleDeviceToken(deviceToken: string | undefined): void {
        const userId = opts.userId;
        const updatedAt = dateNow();

        if (!entry) {
            throw new Error('User does not exist: ' + userId);
        }

        // HOTFIX: Optimistically apply the changes in the DB call below.
        // This is necessary in case the value is used in the same action or in the same batch.
        entry.chatbotMetainfo = entry.chatbotMetainfo ?? {};

        if (deviceToken) {
            entry.chatbotMetainfo.appleDeviceToken = deviceToken;
            entry.chatbotMetainfo.appleDeviceTokenUpdatedAt = updatedAt;
        } else {
            delete entry.chatbotMetainfo.appleDeviceToken;
            delete entry.chatbotMetainfo.appleDeviceTokenUpdatedAt;
        }

        const action = async () => {
            entry.chatbotMetainfo = await db.chatbot.setAppleDeviceToken(userId, deviceToken, updatedAt);
        };

        delayedActions.push({ type: 'action', action, apiMethodName: 'api.chatbot.setAppleDeviceToken' });
    }

    function appleDeviceTokenIsValid() {
        if (!entry) {
            throw new Error(`User does not exist: ${opts.userId}`);
        }

        const tokenExpired = entry.chatbotMetainfo?.appleDeviceTokenUpdatedAt
            ? isAppleDeviceTokenExpired(entry.chatbotMetainfo.appleDeviceTokenUpdatedAt, dateNow())
            : false;

        return !tokenExpired && !!entry.chatbotMetainfo?.appleDeviceToken;
    }

    function setAndroidDeviceToken(deviceToken: string | undefined): void {
        const userId = opts.userId;
        const updatedAt = dateNow();

        if (!entry) {
            throw new Error('User does not exist: ' + userId);
        }

        // HOTFIX: Optimistically apply the changes in the DB call below.
        // This is necessary in case the value is used in the same action or in the same batch.
        entry.chatbotMetainfo = entry.chatbotMetainfo ?? {};

        if (deviceToken) {
            entry.chatbotMetainfo.androidDeviceToken = deviceToken;
            entry.chatbotMetainfo.androidDeviceTokenUpdatedAt = updatedAt;
        } else {
            delete entry.chatbotMetainfo.androidDeviceToken;
            delete entry.chatbotMetainfo.androidDeviceTokenUpdatedAt;
        }

        const action = async () => {
            entry.chatbotMetainfo = await db.chatbot.setAndroidDeviceToken(userId, deviceToken, updatedAt);
        };

        delayedActions.push({ type: 'action', action, apiMethodName: 'api.chatbot.setAndroidDeviceToken' });
    }

    function androidDeviceTokenIsValid() {
        if (!entry) {
            throw new Error(`User does not exist: ${opts.userId}`);
        }

        const tokenExpired = entry.chatbotMetainfo?.androidDeviceTokenUpdatedAt
            ? isAndroidDeviceTokenExpired(entry.chatbotMetainfo.androidDeviceTokenUpdatedAt, dateNow())
            : false;

        return !tokenExpired && !!entry.chatbotMetainfo?.androidDeviceToken;
    }

    function linkRichMenuToUser(linkOpts: { richMenuId: string; userId: string }): void {
        if (platform !== 'line') {
            throw Error('Rich menu linking is only enabled on the LINE platform');
        }

        if (!richMenuAPIClient) {
            throw Error(
                'Rich menu API client is undefined. Make sure to configure the LINE_CHANNEL_ACCESS_TOKEN environment variable: https://docs.dev.gc-internal.net/replicant/Environment/.',
            );
        }

        const action = (): Promise<void> => richMenuAPIClient.linkRichMenuToUser(linkOpts);

        delayedActions.push({ type: 'action', action, apiMethodName: 'api.chatbot.linkRichMenuToUser' });
    }

    function unlinkRichMenuFromUser(unlinkUserId: string): void {
        if (platform !== 'line') {
            throw Error('Rich menu linking is only enabled on the LINE platform');
        }

        if (!richMenuAPIClient) {
            throw Error(
                'Rich menu API client is undefined. Make sure to configure the LINE_CHANNEL_ACCESS_TOKEN environment variable: https://docs.dev.gc-internal.net/replicant/Environment/.',
            );
        }

        const action = (): Promise<void> => richMenuAPIClient.unlinkRichMenuFromUser(unlinkUserId);

        delayedActions.push({ type: 'action', action, apiMethodName: 'api.chatbot.unlinkRichMenuFromUser' });
    }

    function setEmail(email: string) {
        const userId = opts.userId;

        const action = async () => {
            if (!entry) {
                throw new Error('User does not exist: ' + userId);
            }

            entry.chatbotMetainfo = await db.chatbot.setEmail(userId, email);
        };

        delayedActions.push({ type: 'action', action, apiMethodName: 'api.chatbot.setEmail' });
    }

    function setWebPushSubscription(pushSubscription: WebPushSubscription | undefined) {
        const userId = opts.userId;

        const action = async () => {
            if (!entry) {
                throw new Error('User does not exist: ' + userId);
            }

            entry.chatbotMetainfo = await db.chatbot.setWebPushSubscription(userId, pushSubscription);
        };

        delayedActions.push({ type: 'action', action, apiMethodName: 'api.chatbot.setWebPushSubscription' });
    }

    function subscribeSms(consentText: string) {
        const userId = opts.userId;

        const action = async () => {
            if (!consentText) {
                throw new Error('Cannot subscribe SMS without consent text');
            }
            if (!entry) {
                throw new Error('User does not exist: ' + userId);
            }

            if (!entry.metainfo.hasPhoneNumber) {
                throw new Error('Cannot subscribe SMS without phone number');
            }

            entry.chatbotMetainfo = await db.chatbot.subscribeSms(userId);
            await db.accountLink.setNotificationConsentText({
                userId,
                notificationConsentText: consentText,
            });
        };

        delayedActions.push({ type: 'action', action, apiMethodName: 'api.chatbot.subscribeSms' });
    }

    function unsubscribeSms() {
        const userId = opts.userId;

        const action = async () => {
            if (!entry) {
                throw new Error('User does not exist: ' + userId);
            }

            entry.chatbotMetainfo = await db.chatbot.unsubscribeSms(userId);
        };

        delayedActions.push({ type: 'action', action, apiMethodName: 'api.chatbot.unsubscribeSms' });
    }

    return {
        sendFacebookA2UMessage,
        sendMessage,
        sendLineMessage,
        sendInstagramMessage,
        sendMessengerMessage,
        sendDiscordMessage,
        sendTelegramMessage,
        validateSubscription,
        getAppleDeviceTokenUpdatedAt,
        getAndroidDeviceTokenUpdatedAt,
        unsubscribe,
        setAppleDeviceToken,
        appleDeviceTokenIsValid,
        setAndroidDeviceToken,
        androidDeviceTokenIsValid,
        linkRichMenuToUser,
        unlinkRichMenuFromUser,
        setEmail,
        setWebPushSubscription,
        subscribeSms,
        unsubscribeSms,
    };
}

export function renderTelegramMessage(
    message: ChatbotMessageInstance,
    rendererApi: TemplateRendererAPI,
): TelegramMessage {
    const renderer = message.renderers.telegram;

    if (!renderer) {
        throw Error(`No telegram renderer in chatbot message template ${message.name}`);
    }

    return renderer({ api: rendererApi, args: message.args, payload: message.payload });
}
